/**
 * Query infrastructure config — Offline-first foundation (Jul 2026).
 *
 * Every knob is overridable via environment variables so ops can tune
 * caching behaviour per-build without a code change. Defaults follow
 * the approved architecture spec:
 *   staleTime : 5 minutes   (data considered fresh)
 *   gcTime    : 24 hours    (kept in memory/IndexedDB — v5 name for cacheTime)
 */

const int = (raw, fallback) => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const QUERY_STALE_TIME_MS = int(
  process.env.REACT_APP_QUERY_STALE_MINUTES, 5) * 60 * 1000;

// TanStack Query v5 renamed `cacheTime` → `gcTime` (same semantics).
export const QUERY_GC_TIME_MS = int(
  process.env.REACT_APP_QUERY_GC_HOURS, 24) * 60 * 60 * 1000;

// Persisted cache max age — entries older than this are dropped on
// restore. Kept equal to gcTime by default.
export const PERSIST_MAX_AGE_MS = QUERY_GC_TIME_MS;

// Cache "buster": changing this string invalidates every persisted
// entry (used automatically on app version bumps so a deploy never
// rehydrates stale-shaped data into new code).
export const PERSIST_BUSTER =
  process.env.REACT_APP_QUERY_CACHE_BUSTER ||
  process.env.REACT_APP_VERSION ||
  'v1';

// IndexedDB partition (database/store names). Namespaced per app so a
// shared WebView origin can never collide with another app's cache.
export const IDB_DATABASE = 'mesp-query-cache';
export const IDB_STORE = 'tanstack';
export const IDB_KEY = `mesp-rq-${PERSIST_BUSTER}`;

// Opt-in flag: when '1', global query errors surface as sonner toasts
// (requires a <Toaster/> mounted). Default OFF — the app currently has
// no global Toaster and behaviour must not change.
export const TOAST_GLOBAL_ERRORS =
  process.env.REACT_APP_QUERY_TOAST_ERRORS === '1';
